// src/components/Export/ExportPanel.tsx
import React, { useState } from 'react';
import { useProcessing } from '../../context/ProcessingContext';

interface ExportFormat {
  value: string;
  label: string;
  description: string;
}

const CONTOUR_IMAGE_FORMATS: ExportFormat[] = [
  { value: 'png', label: 'PNG', description: 'High resolution raster image' },
  { value: 'jpeg', label: 'JPEG', description: 'Compressed image format' },
  { value: 'pdf', label: 'PDF', description: 'Vector-based document' },
  { value: 'tiff', label: 'TIFF', description: 'High-quality raster format' }
];

const CONTOUR_LINE_FORMATS: ExportFormat[] = [
  { value: 'shp', label: 'Shapefile', description: 'ESRI Shapefile format' },
  { value: 'svg', label: 'SVG', description: 'Scalable Vector Graphics' },
  { value: 'dxf', label: 'DXF', description: 'AutoCAD format' }
];

const GRID_FORMATS: ExportFormat[] = [
  { value: 'grid', label: 'GRID', description: 'ESRI Grid format' },
  { value: 'geotiff', label: 'GeoTIFF', description: 'Georeferenced TIFF' },
  { value: 'img', label: 'IMG', description: 'ERDAS IMAGINE format' },
  { value: 'asc', label: 'ASCII Grid', description: 'Text-based grid format' },
  { value: 'xyz', label: 'XYZ', description: 'Coordinate-based format' },
  { value: 'stl', label: 'STL', description: '3D printable surface mesh' }
];

interface ExportOptions {
  verticalScale?: number;  // For STL export
  resolution?: string;     // For image exports
  quality?: number;        // For JPEG
  vectorScale?: number;    // For vector formats
}

export const ExportPanel: React.FC = () => {
  const { state } = useProcessing();
  const [selectedType, setSelectedType] = useState<string>('');
  const [selectedFormat, setSelectedFormat] = useState<string>('');
  const [exportOptions, setExportOptions] = useState<ExportOptions>({
    verticalScale: 1,
    resolution: '300',
    quality: 90,
    vectorScale: 1
  });
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  if (!state.interpolatedData?.ml?.data) {
    return (
      <div className="panel-section">
        <h2>Export Results</h2>
        <p className="empty-state">Please complete the interpolation step first.</p>
      </div>
    );
  }

  const handleExport = async () => {
    if (!selectedType || !selectedFormat) return;
    
    setIsExporting(true);
    setExportError(null);

    try {
      const response = await fetch('/api/export', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: selectedType,
          format: selectedFormat,
          options: exportOptions,
          data: state.interpolatedData.ml.data,
          contourConfig: state.contourConfig,
          geographicMetadata: state.griddedData?.metadata.geographic_metadata
        }),
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }

      const blob = await response.blob();
      const filename = `export.${selectedFormat}`;
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      setExportError(error instanceof Error ? error.message : 'Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  const renderFormatOptions = () => {
    if (selectedFormat === 'stl') {
      return (
        <div className="control-item">
          <label>Vertical Scale Factor</label>
          <div className="control-input">
            <input
              type="range"
              min="0.1"
              max="10"
              step="0.1"
              value={exportOptions.verticalScale}
              onChange={(e) => setExportOptions({
                ...exportOptions,
                verticalScale: parseFloat(e.target.value)
              })}
            />
            <span className="value">{exportOptions.verticalScale}x</span>
          </div>
          <p className="option-description">
            Adjust the vertical exaggeration of the 3D model
          </p>
        </div>
      );
    }

    if (selectedType === 'image') {
      return (
        <>
          <div className="control-item">
            <label>Resolution (DPI)</label>
            <select
              value={exportOptions.resolution}
              onChange={(e) => setExportOptions({
                ...exportOptions,
                resolution: e.target.value
              })}
              className="export-select"
            >
              <option value="150">150 DPI</option>
              <option value="300">300 DPI</option>
              <option value="600">600 DPI</option>
            </select>
          </div>
          {selectedFormat === 'jpeg' && (
            <div className="control-item">
              <label>Quality</label>
              <div className="control-input">
                <input
                  type="range"
                  min="1"
                  max="100"
                  value={exportOptions.quality}
                  onChange={(e) => setExportOptions({
                    ...exportOptions,
                    quality: parseInt(e.target.value)
                  })}
                />
                <span className="value">{exportOptions.quality}%</span>
              </div>
            </div>
          )}
        </>
      );
    }

    if (['svg', 'dxf', 'ai'].includes(selectedFormat)) {
      return (
        <div className="control-item">
          <label>Vector Scale</label>
          <div className="control-input">
            <input
              type="number"
              min="0.1"
              step="0.1"
              value={exportOptions.vectorScale}
              onChange={(e) => setExportOptions({
                ...exportOptions,
                vectorScale: parseFloat(e.target.value)
              })}
            />
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="panel-section">
      <h2>Export Results</h2>
      
      <div className="panel-content">
        <div className="panel-preview">
          <div className="visualization-container">
            {state.interpolatedData.ml.plot && (
              <img
                src={`data:image/png;base64,${state.interpolatedData.ml.plot}`}
                alt="Current visualization"
                className="preview-image"
              />
            )}
          </div>
        </div>

        <div className="panel-controls">
          <div className="control-group">
            <h3>Export Options</h3>
            
            <div className="control-item">
              <label>Export Type</label>
              <select
                value={selectedType}
                onChange={(e) => {
                  setSelectedType(e.target.value);
                  setSelectedFormat('');
                }}
                className="export-select"
              >
                <option value="">Select type...</option>
                <option value="image">Contoured Image</option>
                <option value="contours">Contour Lines</option>
                <option value="grid">Grid Data</option>
              </select>
            </div>

            {selectedType && (
              <div className="control-item">
                <label>Format</label>
                <select
                  value={selectedFormat}
                  onChange={(e) => setSelectedFormat(e.target.value)}
                  className="export-select"
                >
                  <option value="">Select format...</option>
                  {(selectedType === 'image' ? CONTOUR_IMAGE_FORMATS :
                    selectedType === 'contours' ? CONTOUR_LINE_FORMATS :
                    GRID_FORMATS).map(format => (
                    <option key={format.value} value={format.value}>
                      {format.label} - {format.description}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {selectedFormat && renderFormatOptions()}

            {exportError && (
              <div className="error-message">
                {exportError}
              </div>
            )}

            <button
              className="process-button"
              onClick={handleExport}
              disabled={!selectedType || !selectedFormat || isExporting}
            >
              {isExporting ? (
                <>
                  <span className="processing-spinner" />
                  <span>Exporting...</span>
                </>
              ) : (
                'Export'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportPanel;